import { React, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import { decode as base64_decode, encode as base64_encode } from "base-64";
import { useTranslation } from "react-i18next";
import { signIn } from "../store/reducer";
import * as formik from "formik";
import { ErrorMessage } from "formik";
import * as yup from "yup";
import { T } from "../components/helpful";
import { link } from "../config";

/**
 * ForgotPasswordForm
 *
 * This component renders a form for a user to reset their password.  It
 * takes the username and email of the user as input and sends a POST
 * request to the server to reset the password.  The server's response is
 * displayed to the user.
 *
 * @returns {JSX.Element}
 * @author lionel ghogue ngondeke <lionelladrog@gmail.com> 2024/10/11
 */


export default function ForgotPasswordForm() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [isloading, setIsloading] = useState(false);
  const [isvisible, setIsVisible] = useState(false);
  const [message, setMessage] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { Formik } = formik;

  const schema = yup.object().shape({
    username: yup.string().required(<T value={"Username is required"} />),
    email: yup.string().required(<T value={"Email is required"} />),
  });

  const closeMsg = (e) => {
    e.preventDefault();
    setIsVisible(false);
  };

  function handleOnSubmit(values) {
    setIsloading(true);
    const formData = JSON.stringify({ formData: values });

    fetch(link + "passwordReset", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "*/*",
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 200 && data.result) {
          setMessage(" An email has been sent to your address.");
          setIsVisible(true);
        } else {
          setMessage(data.result);
          setIsVisible(true);

          setTimeout(() => {
            setIsVisible(false);
          }, 5000);
        }
        setIsloading(false);
      })
      .catch((error) => {
        setIsVisible(true);
        setTimeout(() => {
          setIsVisible(false);
        }, 5000);
        setIsloading(false);
      });
  }

  return (
    <div className="">
      <h6 className="text-center text-uppercase mb-4">Forgot password?</h6>

      <Formik
        validationSchema={schema}
        onSubmit={handleOnSubmit}
        initialValues={{
          username: "",
          email: "",
        }}
      >
        {({
          handleSubmit,
          handleChange,

          values,
          touched,
          errors,
          handleBlur,
          isSubmitting,
        }) => (
          <Form noValidate onSubmit={handleSubmit} className="" id="login-form">
            <FloatingLabel label={t("username")} className="mb-3 ">
              <Form.Control
                type="text"
                className=""
                placeholder={t("username")}
                name="username"
                id="username"
                value={values.username}
                onChange={handleChange}
                isInvalid={!!errors.username}
              />
              <Form.Control.Feedback type="invalid">
                {errors.username}
              </Form.Control.Feedback>
            </FloatingLabel>

            <FloatingLabel label={t("email")}>
              <Form.Control
                type="email"
                className=""
                id="email"
                placeholder="Email"
                value={values.email}
                onChange={handleChange}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </FloatingLabel>

            {isvisible ? (
              <div className="password-reset-success d-flex align-items-center  mb-2 ">
                {message}
                <button
                  className="ms-auto btn"
                  onClick={closeMsg}
                  aria-label="Close"
                >
                  <i className="bi bi-x"></i>
                </button>
              </div>
            ) : (
              <div className="row  mt-3 mb-2">
                <div className="col r-margin">
                  <button
                    className="col-12   bcb-btn bcb-btn-primary text-uppercase"
                    type="submit"
                    disabled={isloading}
                  > 
                    {isloading ? (
                      <>
                        <span
                          className="spinner-grow spinner-grow-sm"
                          aria-hidden="true"
                        ></span>
                        <span> Checking...</span>
                      </>
                    ) : (
                      t("reset_password")
                    )}
                  </button>
                </div>
              </div>
            )}

            <div className="text-center text-uppercase">
              <a
                href="/"
                className="bcb-secondary-light-color text-smaller  sign-in-btn "
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/");
                }}
              >
                {t("Sign_in")}
              </a>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
